import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanyAccount } from "@/hooks/useCompanyAccount";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Users, Mail, Shield, Send, Trash2, Clock, CheckCircle2, XCircle, Plus, UserPlus, Loader2, KeyRound, Copy, Check, Play, MessageSquare,
} from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useChatSystem } from "@/hooks/useChatSystem";

import { notifySound } from "@/lib/notifySound";
import { emitJoinRequestsInvalidated } from "@/hooks/useUnreadJoinRequests";
import { useShipperNetworks } from "@/hooks/useShipperNetworks";
import { BrokerGroupsSection } from "@/components/lr-direct/BrokerGroupsSection";
import AddBrokersToNetworkSheet from "@/components/lr-direct/AddBrokersToNetworkSheet";
import { PrivateBrokerNetworkHero } from "@/components/lr-direct/PrivateBrokerNetworkHero";
import { NetworkStatBar } from "@/components/lr-direct/NetworkStatBar";
import { GettingStartedCard } from "@/components/lr-direct/GettingStartedCard";
import { HelpGuideModal } from "@/components/lr-direct/HelpGuideModal";
import { BrokerManageGroupsPopover } from "@/components/lr-direct/BrokerManageGroupsPopover";
import { BulkInviteBrokersCard } from "@/components/lr-direct/BulkInviteBrokersCard";
import { RemoveBrokerFromNetworkDialog } from "@/components/lr-direct/RemoveBrokerFromNetworkDialog";
import { useShipperMyLoadsCount } from "@/hooks/useShipperMyLoadsCount";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { displayCompanyName } from "@/lib/displayCompanyName";

const DEMO_VIDEO_URL = "https://youtu.be/pndY6Ns1zR0";

interface NetworkRow {
  id: string;
  name: string;
  is_default: boolean;
  created_at: string;
}
interface MemberRow {
  id: string;
  broker_user_id: string;
  broker_company_id: string | null;
  added_at: string;
  profile?: { first_name: string | null; last_name: string | null; email: string | null } | null;
  company?: { name: string | null } | null;
}
interface InviteRow {
  id: string;
  invited_email: string;
  status: string;
  message: string | null;
  expires_at: string;
  created_at: string;
  responded_at: string | null;
}
interface JoinRequestRow {
  id: string;
  broker_user_id: string;
  broker_company_id: string | null;
  message: string | null;
  status: string;
  requested_at: string;
  brokerProfile?: { first_name: string | null; last_name: string | null; email: string | null } | null;
  brokerCompany?: { name: string | null } | null;
}

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function LRDirectNetworkSettingsPage() {
  usePageMeta({
    title: "LR Direct — Private Network | LaneRunner",
    description: "Manage your private broker network for LR Direct.",
  });

  const { user, profile } = useAuth();
  const { company } = useCompanyAccount();
  const { openChatWithUser } = useChatSystem();
  const [searchParams] = useSearchParams();


  const [loading, setLoading] = useState(true);
  const [network, setNetwork] = useState<NetworkRow | null>(null);
  const [selectedNetworkId, setSelectedNetworkId] = useState<string | null>(null);
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [invites, setInvites] = useState<InviteRow[]>([]);
  const [joinRequests, setJoinRequests] = useState<JoinRequestRow[]>([]);
  const [respondingId, setRespondingId] = useState<string | null>(null);
  const [approveTargetNetwork, setApproveTargetNetwork] = useState<Record<string, string>>({});
  const [addBrokersOpen, setAddBrokersOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [brokersConnected, setBrokersConnected] = useState(0);
  const [createSignal, setCreateSignal] = useState(0);

  const isShipper = profile?.user_type === "shipper";
  const activeLoadsCount = useShipperMyLoadsCount(user?.id, isShipper);

  const {
    networks: allNetworks,
    reload: reloadNetworks,
    createNetwork,
    updateNetwork,
    deleteNetwork,
    setDefaultNetwork,
  } = useShipperNetworks(user?.id);

  // Aggregate: brokers connected across all of this shipper's networks.
  useEffect(() => {
    if (!user?.id || allNetworks.length === 0) {
      setBrokersConnected(0);
      return;
    }
    let cancelled = false;
    (async () => {
      const ids = allNetworks.map((n) => n.id);
      const { data } = await supabase
        .from("shipper_network_brokers" as any)
        .select("broker_user_id")
        .in("network_id", ids)
        .eq("status", "accepted")
        .is("removed_at", null);
      if (cancelled) return;
      const unique = new Set(((data as any[]) || []).map((r) => r.broker_user_id));
      setBrokersConnected(unique.size);
    })();
    return () => { cancelled = true; };
  }, [user?.id, allNetworks, members.length]);

  // Keep selected network valid as the list changes.
  useEffect(() => {
    if (allNetworks.length === 0) {
      setSelectedNetworkId(null);
      return;
    }
    if (!selectedNetworkId || !allNetworks.find((n) => n.id === selectedNetworkId)) {
      const def = allNetworks.find((n) => n.is_default) || allNetworks[0];
      setSelectedNetworkId(def.id);
    }
  }, [allNetworks, selectedNetworkId]);





  const loadPendingJoinRequests = useCallback(async () => {
    if (!company?.id) { setJoinRequests([]); return; }
    const { data: reqRows } = await supabase
      .from("shipper_network_join_requests" as any)
      .select("id, broker_user_id, broker_company_id, message, status, requested_at")
      .eq("shipper_company_id", company.id)
      .eq("status", "pending")
      .order("requested_at", { ascending: false });
    const reqs = ((reqRows as any[]) || []) as JoinRequestRow[];
    const brokerIds = reqs.map((r) => r.broker_user_id).filter(Boolean);
    const brokerCoIds = reqs.map((r) => r.broker_company_id).filter(Boolean) as string[];
    const [{ data: bps }, { data: bcs }] = await Promise.all([
      brokerIds.length
        ? supabase.from("profiles").select("id, first_name, last_name, email").in("id", brokerIds)
        : Promise.resolve({ data: [] as any[] }),
      brokerCoIds.length
        ? supabase.from("companies").select("id, name").in("id", brokerCoIds)
        : Promise.resolve({ data: [] as any[] }),
    ]);
    const bpMap = new Map((bps as any[] || []).map((p) => [p.id, p]));
    const bcMap = new Map((bcs as any[] || []).map((c) => [c.id, c]));
    setJoinRequests(
      reqs.map((r) => ({
        ...r,
        brokerProfile: bpMap.get(r.broker_user_id) ?? null,
        brokerCompany: r.broker_company_id ? bcMap.get(r.broker_company_id) ?? null : null,
      }))
    );
  }, [company?.id]);

  const loadAll = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    const net = allNetworks.find((n) => n.id === selectedNetworkId)
      || allNetworks.find((n) => n.is_default)
      || allNetworks[0]
      || null;
    setNetwork(net ? { id: net.id, name: net.name, is_default: net.is_default, created_at: net.created_at } : null);

    if (!net) {
      setMembers([]);
      setInvites([]);
      // Brand-new shipper without a network yet must still see incoming
      // broker requests — approve auto-creates a default network server-side.
      await loadPendingJoinRequests();
      setLoading(false);
      return;
    }

    const [{ data: memberRows }, { data: inviteRows }] = await Promise.all([
      supabase
        .from("shipper_network_brokers" as any)
        .select("id, broker_user_id, broker_company_id, added_at")
        .eq("network_id", net.id)
        .eq("status", "accepted")
        .is("removed_at", null)
        .order("added_at", { ascending: false }),
      supabase
        .from("shipper_network_invitations" as any)
        .select("id, invited_email, status, message, expires_at, created_at, responded_at")
        .eq("network_id", net.id)
        .order("created_at", { ascending: false })
        .limit(50),
    ]);

    const memberList = ((memberRows as any[]) || []) as MemberRow[];

    // Enrich with profile + company names
    const userIds = memberList.map((m) => m.broker_user_id).filter(Boolean);
    const companyIds = memberList.map((m) => m.broker_company_id).filter(Boolean) as string[];
    const [{ data: profiles }, { data: companies }] = await Promise.all([
      userIds.length
        ? supabase.from("profiles").select("id, first_name, last_name, email").in("id", userIds)
        : Promise.resolve({ data: [] as any[] }),
      companyIds.length
        ? supabase.from("companies").select("id, name").in("id", companyIds)
        : Promise.resolve({ data: [] as any[] }),
    ]);
    const profileMap = new Map((profiles as any[] || []).map((p) => [p.id, p]));
    const companyMap = new Map((companies as any[] || []).map((c) => [c.id, c]));
    setMembers(
      memberList.map((m) => ({
        ...m,
        profile: profileMap.get(m.broker_user_id) ?? null,
        company: m.broker_company_id ? companyMap.get(m.broker_company_id) ?? null : null,
      }))
    );
    setInvites(((inviteRows as any[]) || []) as InviteRow[]);

    // Load pending join requests for the shipper's company
    await loadPendingJoinRequests();

    setLoading(false);
  }, [user, company?.id, selectedNetworkId, allNetworks, loadPendingJoinRequests]);



  useEffect(() => { loadAll(); }, [loadAll]);

  // Realtime: new join requests for this shipper's company.
  useEffect(() => {
    if (!company?.id) return;
    const channel = supabase
      .channel(`network-join-requests-${company.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "shipper_network_join_requests",
          filter: `shipper_company_id=eq.${company.id}`,
        },
        async (payload) => {
          const row = payload.new as { broker_user_id?: string; broker_company_id?: string | null };
          // Resolve broker name for the toast.
          let brokerName = "a broker";
          try {
            if (row?.broker_company_id) {
              const { data: c } = await supabase
                .from("companies").select("name").eq("id", row.broker_company_id).maybeSingle();
              if (c?.name) brokerName = c.name;
            }
            if (brokerName === "a broker" && row?.broker_user_id) {
              const { data: p } = await supabase
                .from("profiles").select("first_name, last_name, email").eq("id", row.broker_user_id).maybeSingle();
              if (p) {
                const n = [p.first_name, p.last_name].filter(Boolean).join(" ");
                brokerName = n || p.email || brokerName;
              }
            }
          } catch { /* noop */ }

          notifySound.play("alert-ping");
          await loadAll();
          toast.success(`New join request from ${brokerName}`, {
            duration: 10000,
            action: {
              label: "View",
              onClick: () => {
                document.getElementById("pending-join-requests")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              },
            },
          });
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "shipper_network_join_requests",
          filter: `shipper_company_id=eq.${company.id}`,
        },
        () => { loadAll(); }
      )
      .subscribe((status) => {
        // Self-heal on (re)subscribe — closes CHANNEL_ERROR gaps so a
        // request/response that landed while disconnected still surfaces.
        if (status === "SUBSCRIBED") { loadAll(); }
      });

    // Window focus / tab-visible self-heal — mirrors the DM pattern in
    // ChatContext so returning to the tab immediately reflects new state.
    let focusTimer: ReturnType<typeof setTimeout> | null = null;
    const runDebounced = () => {
      if (focusTimer) return;
      focusTimer = setTimeout(() => { focusTimer = null; loadAll(); }, 500);
    };
    const onFocus = () => runDebounced();
    const onVis = () => { if (document.visibilityState === "visible") runDebounced(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
      if (focusTimer) clearTimeout(focusTimer);
    };
  }, [company?.id, loadAll]);

  const handleRespondRequest = async (requestId: string, action: "approve" | "decline") => {
    setRespondingId(requestId);
    try {
      const targetNetworkId = action === "approve"
        ? (approveTargetNetwork[requestId] || selectedNetworkId || network?.id || undefined)
        : undefined;
      const { data, error } = await supabase.functions.invoke("respond-network-request", {
        body: { requestId, action, networkId: targetNetworkId },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success(action === "approve" ? "Broker added to your network" : "Request declined");
      // Clear the TopBar badge immediately — don't wait for realtime UPDATE.
      emitJoinRequestsInvalidated();
      await Promise.all([loadAll(), reloadNetworks()]);
    } catch (e: any) {
      toast.error(e?.message || "Failed to respond");
    } finally {
      setRespondingId(null);
    }
  };




  const invokeManage = async (body: Record<string, unknown>) => {
    const { data, error } = await supabase.functions.invoke("manage-network-membership", { body });
    const err = error || (data as any)?.error;
    if (err) {
      const msg = typeof err === "string" ? err : (err.message || "Action failed");
      return { ok: false as const, message: msg, data };
    }
    return { ok: true as const, data };
  };

  const handleRevokeInvite = async (inviteId: string) => {
    const res = await invokeManage({ action: "revoke_invite", inviteId });
    if (!res.ok) { toast.error(res.message); return; }
    toast.success("Invitation revoked");
    loadAll();
  };

  const handleDeleteInvite = async (inviteId: string) => {
    const res = await invokeManage({ action: "delete_invite", inviteId });
    if (!res.ok) { toast.error(res.message); loadAll(); return; }
    setInvites((prev) => prev.filter((i) => i.id !== inviteId));
    toast.success("Invitation removed");
  };

  const handleClearCompletedInvites = async () => {
    const completed = invites.filter((i) => i.status !== "pending");
    if (completed.length === 0) { toast.info("No completed invitations to clear"); return; }
    const shipperCompanyId = company?.id;
    if (!shipperCompanyId) { toast.error("Missing company context"); return; }
    const res = await invokeManage({ action: "clear_completed_invites", shipperCompanyId });
    if (!res.ok) { toast.error(res.message); return; }
    const deletedCount = ((res.data as any)?.deleted as number) ?? 0;
    setInvites((prev) => prev.filter((i) => i.status === "pending"));
    toast.success(`Cleared ${deletedCount} invitation${deletedCount === 1 ? "" : "s"}`);
  };



  // NOTE: The old handleRemoveMember was a group-scoped soft-delete disguised
  // as a full-network removal. It is now split into two explicit controls:
  //   - BrokerManageGroupsPopover: safe multi-group membership editing
  //   - RemoveBrokerFromNetworkDialog: destructive, warned, full removal
  // Both live directly on each member row. This handler is intentionally gone.

  if (!isShipper) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Private networks are managed by shippers.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="w-full py-2 space-y-5 sm:space-y-6">
        {/* Header with hero graphic */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="min-w-0 max-w-2xl">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
              Private Broker Network
            </h1>
            <p className="mt-1.5 text-sm sm:text-base text-muted-foreground">
              Invite trusted brokers. Post freight to them first, before it reaches the open market.
            </p>
          </div>
          <div className="flex flex-col md:items-end gap-3 w-full md:w-auto">
            <PrivateBrokerNetworkHero className="hidden md:block w-[360px] h-[150px] shrink-0" />
          </div>
        </div>

        {/* Stat bar */}
        <NetworkStatBar
          networks={allNetworks.length}
          brokersConnected={brokersConnected}
          pendingRequests={joinRequests.length}
          activeLoads={activeLoadsCount}
        />

        {/* Top row: Network ID | Getting Started | Create Group + Help */}
        <div className="grid gap-4 md:gap-5 md:grid-cols-3">
          <PrivateNetworkIdCard companyId={company?.id} />
          <GettingStartedCard onOpenHelp={() => setHelpOpen(true)} demoUrl={DEMO_VIDEO_URL} />
          <BulkInviteBrokersCard networkId={network?.id} onSent={loadAll} />
        </div>

        {/* Row: Broker Groups | Invite a Broker */}
        <div className="flex flex-col gap-4 md:gap-5">
          <BrokerGroupsSection
            networks={allNetworks}
            shipperUserId={user?.id ?? ""}
            shipperCompanyId={company?.id ?? null}
            onCreate={async (v) => { await createNetwork(v.name, v.description, v.is_default, v.color); }}
            onUpdate={async (id, v) => { await updateNetwork(id, v); }}
            onDelete={async (id) => {
              await deleteNetwork(id);
              if (selectedNetworkId === id) setSelectedNetworkId(null);
            }}
            onSetDefault={async (id) => { await setDefaultNetwork(id); }}
            externalCreateSignal={createSignal}
            onMutated={async () => { await reloadNetworks(); await loadAll(); }}
          />
        </div>


        {/* Row: Pending Join Requests | Network Members */}
        <div className="flex flex-col gap-4 md:gap-5">
        {/* Pending join requests (broker → shipper) */}
        <Card id="pending-join-requests" className="overflow-hidden border-amber-200/40">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between bg-amber-50/40">
            <h2 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-amber-600" /> Pending Join Requests
            </h2>
            <Badge variant="secondary" className="text-[10px]">{joinRequests.length}</Badge>
          </div>
          {loading ? (
            <div className="p-5 space-y-2">
              {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : joinRequests.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              No pending requests. Brokers who find you in the LR Direct directory can request to join here.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {joinRequests.map((r) => {
                const name = [r.brokerProfile?.first_name, r.brokerProfile?.last_name].filter(Boolean).join(" ");
                const busy = respondingId === r.id;
                return (
                  <li key={r.id} className="p-4 flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm text-foreground">
                          {r.brokerCompany?.name || name || r.brokerProfile?.email || "Broker"}
                        </span>
                        <Badge className="bg-amber-500/15 text-amber-600 border-0 text-[10px] uppercase font-bold gap-1">
                          <Clock className="h-3 w-3" />Pending
                        </Badge>
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {name && r.brokerProfile?.email ? `${name} · ${r.brokerProfile.email}` : r.brokerProfile?.email || ""}
                        {" · "}Requested {formatDate(r.requested_at)}
                      </div>
                      {r.message && (
                        <div className="mt-2 rounded-md bg-muted/40 border border-border p-2.5 text-xs text-foreground italic">
                          "{r.message}"
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto sm:shrink-0 sm:items-center">
                      {allNetworks.length > 1 && (
                        <Select
                          value={approveTargetNetwork[r.id] || selectedNetworkId || ""}
                          onValueChange={(v) => setApproveTargetNetwork((m) => ({ ...m, [r.id]: v }))}
                        >
                          <SelectTrigger className="h-9 w-full sm:w-[180px] text-xs">
                            <SelectValue placeholder="Add to network..." />
                          </SelectTrigger>
                          <SelectContent>
                            {allNetworks.map((n) => (
                              <SelectItem key={n.id} value={n.id} className="text-xs">
                                {n.name}{n.is_default ? " (default)" : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <Button
                        size="sm"
                        onClick={() => handleRespondRequest(r.id, "approve")}
                        disabled={busy}
                        className="gap-1.5 text-white w-full sm:w-auto h-11 sm:h-9"
                        style={{ backgroundColor: "hsl(var(--brand-teal))" }}
                      >
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRespondRequest(r.id, "decline")}
                        disabled={busy}
                        className="gap-1.5 w-full sm:w-auto h-11 sm:h-9"
                      >
                        <XCircle className="h-3.5 w-3.5" /> Decline
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        {/* Members */}
        <Card className="overflow-hidden">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between gap-2 flex-wrap">
            <h2 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
              <Users className="h-4 w-4 text-brand-teal" /> Network Members
              {network && (
                <span className="text-[11px] font-medium normal-case tracking-normal text-muted-foreground">
                  · {network.name}
                </span>
              )}
              <Badge variant="secondary" className="text-[10px] ml-1">{members.length}</Badge>
            </h2>
            {network && (
              <Button
                size="sm"
                onClick={() => setAddBrokersOpen(true)}
                className="gap-1.5 text-white h-8"
                style={{ backgroundColor: "hsl(var(--brand-teal))" }}
              >
                <UserPlus className="h-3.5 w-3.5" /> Add Existing Brokers
              </Button>
            )}
          </div>
          {loading ? (
            <div className="p-5 space-y-2">
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : members.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No brokers in your network yet. Send your first invitation above.
            </div>
          ) : (<div className="w-full">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40">
                  <TableHead className="text-[11px]">Broker</TableHead>
                  <TableHead className="text-[11px] hidden sm:table-cell">Company</TableHead>
                  <TableHead className="text-[11px] hidden md:table-cell">Joined</TableHead>
                  <TableHead className="text-[11px] w-[140px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((m) => {
                  const name = [m.profile?.first_name, m.profile?.last_name].filter(Boolean).join(" ");
                  return (
                    <TableRow key={m.id}>
                      <TableCell className="text-[12px] max-w-0">
                        <div className="font-medium truncate">{name || m.profile?.email || "Broker"}</div>
                        {name && m.profile?.email && (
                          <div className="text-[10px] text-muted-foreground truncate">{m.profile.email}</div>
                        )}
                        <div className="text-[10px] text-muted-foreground sm:hidden truncate">
                          {displayCompanyName(m.company?.name)}
                        </div>
                      </TableCell>
                      <TableCell className="text-[12px] hidden sm:table-cell">{displayCompanyName(m.company?.name) || "—"}</TableCell>
                      <TableCell className="text-[12px] text-muted-foreground hidden md:table-cell">{formatDate(m.added_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            title="Message broker"
                            className="h-8 w-8 text-muted-foreground hover:text-foreground"
                            onClick={() => openChatWithUser(
                              m.broker_user_id,
                              displayCompanyName(m.company?.name) || name || m.profile?.email || "Broker",
                              undefined,
                              undefined
                            )}
                          >
                            <MessageSquare className="h-4 w-4" />
                            <span className="sr-only">Message broker</span>
                          </Button>
                          <BrokerManageGroupsPopover
                            brokerUserId={m.broker_user_id}
                            brokerLabel={name || m.profile?.email || "Broker"}
                            networks={allNetworks.map((n) => ({ id: n.id, name: n.name, is_default: n.is_default }))}
                            onSaved={loadAll}
                          />
                          <span className="h-5 w-px bg-border mx-0.5" aria-hidden />
                          <RemoveBrokerFromNetworkDialog
                            brokerUserId={m.broker_user_id}
                            brokerLabel={name || m.profile?.email || "Broker"}
                            networks={allNetworks.map((n) => ({ id: n.id, name: n.name }))}
                            shipperName={displayCompanyName(company?.name) || "a shipper"}
                            onRemoved={loadAll}
                          />
                        </div>
                      </TableCell>

                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
          )}

        </Card>
        </div>

        {/* Invitations */}
        <Card className="overflow-hidden">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between gap-2 flex-wrap">
            <h2 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
              <Mail className="h-4 w-4 text-brand-teal" /> Invitations
              <Badge variant="secondary" className="text-[10px]">{invites.length}</Badge>
            </h2>
            {invites.some((i) => i.status !== "pending") && (
              <Button
                size="sm"
                variant="ghost"
                className="h-8 gap-1.5 text-muted-foreground hover:text-destructive"
                onClick={handleClearCompletedInvites}
              >
                <Trash2 className="h-3.5 w-3.5" /> Clear completed
              </Button>
            )}
          </div>
          {loading ? (
            <div className="p-5 space-y-2">
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : invites.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No invitations sent yet.
            </div>
          ) : (
            <div className="w-full">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40">
                  <TableHead className="text-[11px]">Email</TableHead>
                  <TableHead className="text-[11px]">Status</TableHead>
                  <TableHead className="text-[11px] hidden sm:table-cell">Sent</TableHead>
                  <TableHead className="text-[11px] hidden md:table-cell">Expires</TableHead>
                  <TableHead className="text-[11px] w-[110px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invites.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell className="text-[12px] font-medium max-w-0">
                      <div className="truncate">{inv.invited_email}</div>
                      <div className="text-[10px] text-muted-foreground sm:hidden">
                        Sent {formatDate(inv.created_at)}
                      </div>
                    </TableCell>
                    <TableCell><InviteStatusBadge status={inv.status} /></TableCell>
                    <TableCell className="text-[12px] text-muted-foreground hidden sm:table-cell">{formatDate(inv.created_at)}</TableCell>
                    <TableCell className="text-[12px] text-muted-foreground hidden md:table-cell">{formatDate(inv.expires_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-0.5">
                        {inv.status === "pending" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-muted-foreground hover:text-destructive"
                            onClick={() => handleRevokeInvite(inv.id)}
                          >
                            Revoke
                          </Button>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Remove from list"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() => handleDeleteInvite(inv.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          <span className="sr-only">Remove invitation</span>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}
        </Card>

      </div>

      {user && network && (
        <AddBrokersToNetworkSheet
          open={addBrokersOpen}
          onOpenChange={setAddBrokersOpen}
          targetNetworkId={network.id}
          allNetworks={allNetworks.map((n) => ({ id: n.id, name: n.name }))}
          shipperUserId={user.id}
          shipperCompanyId={company?.id ?? null}
          onAdded={async () => {
            setAddBrokersOpen(false);
            await Promise.all([loadAll(), reloadNetworks()]);
          }}
        />
      )}
    </div>
  );
}

function InviteStatusBadge({ status }: { status: string }) {
  switch (status) {
    case "pending":
      return (
        <Badge className="bg-amber-500/15 text-amber-500 border-0 text-[10px] uppercase font-bold gap-1">
          <Clock className="h-3 w-3" /> Pending
        </Badge>
      );
    case "accepted":
      return (
        <Badge className="bg-emerald-500/15 text-emerald-500 border-0 text-[10px] uppercase font-bold gap-1">
          <CheckCircle2 className="h-3 w-3" /> Accepted
        </Badge>
      );
    case "declined":
      return (
        <Badge className="bg-muted text-muted-foreground border-0 text-[10px] uppercase font-bold gap-1">
          <XCircle className="h-3 w-3" /> Declined
        </Badge>
      );
    case "revoked":
      return <Badge className="bg-muted text-muted-foreground border-0 text-[10px] uppercase font-bold">Revoked</Badge>;
    case "expired":
      return <Badge className="bg-muted text-muted-foreground border-0 text-[10px] uppercase font-bold">Expired</Badge>;
    default:
      return <Badge className="bg-muted text-muted-foreground border-0 text-[10px] uppercase font-bold">{status}</Badge>;
  }
}

function PrivateNetworkIdCard({ companyId }: { companyId: string | undefined }) {
  const [privateId, setPrivateId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!companyId) { setPrivateId(null); return; }
    (async () => {
      const { data } = await supabase
        .from("companies")
        .select("lr_direct_private_id")
        .eq("id", companyId)
        .maybeSingle();
      if (!cancelled) setPrivateId((data as any)?.lr_direct_private_id ?? null);
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  const handleCopy = async () => {
    if (!privateId) return;
    try {
      await navigator.clipboard.writeText(privateId);
      setCopied(true);
      toast.success("Private Network ID copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy to clipboard");
    }
  };

  return (
    <Card className="p-5 border-brand-teal/30 bg-brand-teal/[0.03]">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-teal/10 shrink-0">
          <KeyRound className="h-4 w-4 text-brand-teal" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-bold uppercase tracking-wider">Your Private Network ID</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Share this ID with brokers you want in your private network. They use it to find you and request to join — your company is not listed publicly.
          </p>
          <div className="flex items-center gap-2 mt-3">
            <div className="px-3 py-2 rounded-md bg-background border border-border font-mono text-base tracking-widest font-bold text-foreground select-all">
              {privateId || "—"}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleCopy}
              disabled={!privateId}
              className="gap-1.5 h-9"
            >
              {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

